import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarClock, CheckCircle2, CreditCard, Globe2, RefreshCw, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { BAILLEUR_LINKS } from './links';
import { bailleurApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TID } from '@/constants/testIds';

const STANDARD_PRICE_USD = 3;

function errorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return 'Impossible de lancer le paiement. Réessayez dans un instant.';
}

export default function BailleurAbonnement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => bailleurApi.subscription().then((response) => setData(response.data)).catch(() => toast.error('Impossible de charger les informations d’abonnement.'));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const intentId = searchParams.get('billing_intent');
    if (!intentId) return;
    let cancelled = false;
    let timer;
    let tries = 0;

    const sync = async () => {
      setSyncing(true);
      try {
        const response = await bailleurApi.paymentSync(intentId);
        if (cancelled) return;
        const status = response?.data?.status;
        const flow = response?.data?.flow;
        if (status === 'paid') {
          toast.success(flow === 'activation' ? 'Abonnement activé. Bienvenue sur LoyerBook.' : 'Abonnement renouvelé avec succès.');
          setSearchParams({}, { replace: true });
          await load();
          setSyncing(false);
          if (flow === 'activation') navigate('/bailleur', { replace: true });
          return;
        }
        if (status === 'failed' || status === 'cancelled') {
          toast.error(status === 'failed' ? 'Le paiement a échoué.' : 'Le paiement a été annulé.');
          setSearchParams({}, { replace: true });
          setSyncing(false);
          return;
        }
        tries += 1;
        if (tries < 10) timer = window.setTimeout(sync, 2500);
        else { toast.info('Votre paiement est encore en cours de confirmation.'); setSyncing(false); }
      } catch {
        tries += 1;
        if (tries < 6) timer = window.setTimeout(sync, 3000);
        else setSyncing(false);
      }
    };

    sync();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, [searchParams, setSearchParams, navigate]);

  const pay = async () => {
    setLoading(true);
    try {
      const response = await bailleurApi.renewSubscription();
      const checkoutUrl = response?.data?.checkout_url;
      if (!checkoutUrl) throw new Error('checkout_missing');
      window.location.assign(checkoutUrl);
    } catch (error) {
      toast.error(errorMessage(error));
      setLoading(false);
    }
  };

  const subscription = data?.subscription;
  const offer = data?.offers?.[0];
  const used = Number(data?.used || 0);
  const expiresAt = subscription?.expires_at ? new Date(subscription.expires_at) : null;
  const validExpiry = expiresAt && !Number.isNaN(expiresAt.getTime());
  const marketReady = Boolean(offer?.paiement_actif);
  const standardLocalAmount = Number(offer?.prix_local || 0);
  const currency = offer?.devise_locale || offer?.devise || subscription?.currency || 'USD';
  const paidPriceUsd = Number(subscription?.price_usd || STANDARD_PRICE_USD);
  const promoEligible = Boolean(subscription?.launch_promo_eligible) || paidPriceUsd === 1;
  const promoRank = subscription?.launch_promo_rank;
  const pending = !subscription || subscription?.status === 'pending_payment' || subscription?.payment_method === 'pending';
  const active = subscription?.status === 'active' || subscription?.status === 'grace';
  const checkoutPriceUsd = pending && promoEligible ? 1 : STANDARD_PRICE_USD;
  const expectedLocalAmount = standardLocalAmount > 0 ? standardLocalAmount * (checkoutPriceUsd / STANDARD_PRICE_USD) : null;
  const localPriceLabel = expectedLocalAmount != null
    ? `${expectedLocalAmount.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency}`
    : 'Le montant sera affiché au paiement';

  return (
    <AppLayout links={BAILLEUR_LINKS} testid={TID.bailleurSidebar} title="Mon abonnement">
      {!data ? (
        <div className="lb-card p-8 text-center text-sm text-slate-500">Chargement de votre abonnement…</div>
      ) : (
        <div className="mx-auto max-w-4xl space-y-5 sm:space-y-6">
          {promoEligible && (
            <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400 text-slate-950"><Sparkles className="h-5 w-5" /></div>
                <div><div className="text-xs font-black uppercase tracking-[0.16em] text-amber-700">Offre lancement · place{promoRank ? ` #${promoRank}` : ''}</div><div className="mt-1 text-2xl font-black text-slate-950">1 USD le premier mois</div><p className="mt-1 text-sm text-amber-900">Vous faites partie des 1000 premiers bailleurs. Après ce premier mois, votre abonnement passe à <strong>3 USD / mois</strong>.</p></div>
              </div>
            </div>
          )}

          {pending && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900 sm:p-5">
              <strong>Dernière étape :</strong> votre compte existe déjà. Finalisez le premier paiement pour ouvrir votre espace bailleur.
            </div>
          )}

          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="bg-gradient-to-br from-[#0D1B2A] to-[#16324F] p-5 text-white sm:p-8">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-300">Votre abonnement · {offer?.country || subscription?.country || 'CD'}</div>
                  <h2 className="mt-2 text-3xl font-black">LoyerBook</h2>
                  <div className="mt-4 flex flex-wrap items-end gap-x-3 gap-y-1"><span className="text-4xl font-black">${checkoutPriceUsd} USD</span><span className="pb-1 text-sm text-white/60">{pending && promoEligible ? 'le premier mois' : '/ mois'}</span></div>
                  {pending && promoEligible ? <div className="mt-2 text-sm font-bold text-amber-300">Puis 3 USD / mois à partir du 2e mois</div> : <div className="mt-2 text-sm text-white/65">Renouvellement : <strong className="text-white">3 USD / mois</strong></div>}
                  <div className="mt-2 text-sm text-white/65">Dans votre devise : <strong className="text-white">{localPriceLabel}</strong></div>
                </div>
                <Badge className={`w-fit border capitalize ${pending ? 'border-amber-300/30 bg-amber-300/15 text-amber-100' : 'border-emerald-400/30 bg-emerald-400/15 text-emerald-200'} hover:bg-transparent`}>
                  {pending ? 'Paiement requis' : subscription?.status || 'active'}
                </Badge>
              </div>

              <div className="mt-7 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl bg-white/10 p-4"><Users className="h-5 w-5 text-emerald-300" /><div className="mt-2 text-sm font-bold">Locataires illimités</div><div className="mt-1 text-xs text-white/60">{used} actuellement géré{used > 1 ? 's' : ''}</div></div>
                <div className="rounded-2xl bg-white/10 p-4"><CalendarClock className="h-5 w-5 text-blue-300" /><div className="mt-2 text-sm font-bold">Abonnement mensuel</div><div className="mt-1 text-xs text-white/60">{active && validExpiry ? `Valable jusqu’au ${expiresAt.toLocaleDateString('fr-FR')}` : 'Commence après confirmation du paiement'}</div></div>
                <div className="rounded-2xl bg-white/10 p-4"><Globe2 className="h-5 w-5 text-amber-300" /><div className="mt-2 text-sm font-bold">Prix après le 1er mois</div><div className="mt-1 text-xs text-white/60">3 USD / mois</div></div>
              </div>
            </div>

            <div className="p-5 sm:p-8">
              <h3 className="text-lg font-bold text-slate-900">Tout est inclus</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {['Nombre de locataires illimité', 'Suivi des loyers et échéances', 'Paiements et reçus PDF', 'Calendrier bailleur', 'Factures eau & électricité', 'Tableau de bord et historique'].map((item) => <div key={item} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />{item}</div>)}
              </div>

              <Button onClick={pay} disabled={loading || syncing || !marketReady} className="mt-7 h-12 w-full bg-blue-600 text-base font-bold hover:bg-blue-700 disabled:bg-slate-300 sm:w-auto sm:px-8">
                {syncing ? <RefreshCw className="mr-2 h-5 w-5 animate-spin" /> : <CreditCard className="mr-2 h-5 w-5" />}
                {syncing ? 'Confirmation du paiement…' : loading ? 'Ouverture du paiement…' : marketReady ? (pending ? `Payer et accéder — ${checkoutPriceUsd} USD` : 'Renouveler — 3 USD') : 'Paiement indisponible dans votre pays'}
              </Button>
              <p className="mt-3 text-xs leading-relaxed text-slate-500">Les 1000 premiers bénéficient de 1 USD uniquement pour leur premier mois. Les mois suivants sont à 3 USD. Le montant dans votre devise s’affiche automatiquement selon votre pays.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" /><span>Si un paiement est interrompu, votre compte reste enregistré. Vous pouvez revenir ici et reprendre le paiement.</span></div>
        </div>
      )}
    </AppLayout>
  );
}
